/**
 * Business defaults flat region per scope §6.2. Atlas Day 2 Phase 4.
 *
 * Surface: "Business defaults — universal engine inputs ISG hasn't varied
 * per entity. Key dials: min_roi_pct, returns_reserve_pct,
 * default_lead_time_days. ← overridden by per-entity rows"
 *
 * Bidirectional override pointer per scope §3 + §6.2 — Business defaults
 * surface notes "← overridden by per-entity rows"; PerDistributorCard
 * (etc.) sub-regions could note "overrides business defaults →" (Day 3
 * iteration). Day 2: Business defaults side only.
 *
 * Click handler stub for Phase 4 (Phase 6 wires inspect navigation per B.3).
 */

import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings as SettingsIcon } from 'lucide-react';
import type { BusinessDefaultsSubstrate } from '../../../lib/atlas/substrateCalcSubstrate';

export interface BusinessDefaultsRegionProps {
  substrate: BusinessDefaultsSubstrate;
}

export default function BusinessDefaultsRegion({
  substrate,
}: BusinessDefaultsRegionProps): JSX.Element {
  const navigate = useNavigate();
  const { minRoiPct, returnsReservePct, defaultLeadTimeDays } = substrate;
  return (
    <button
      type="button"
      data-sub-region-id="business-defaults"
      onClick={() => navigate('/admin/engine-atlas/inspect/business-defaults')}
      className="w-full text-left border border-border-subtle bg-surface-0 rounded-md p-3 hover:border-accent transition-colors cursor-pointer"
    >
      <div className="flex items-start gap-2">
        <SettingsIcon size={16} className="text-ink-secondary flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between">
            <h3 className="text-sm font-semibold text-ink-bright">Business defaults</h3>
            <span className="text-xs text-ink-muted">← overridden by per-entity rows</span>
          </div>
          <p className="text-xs text-ink-secondary mt-1">
            Universal engine inputs ISG hasn&apos;t varied per entity. Key dials:{' '}
            <span className="font-mono">
              min_roi_pct={minRoiPct ?? '—'}%, returns_reserve_pct={returnsReservePct ?? '—'}%,
              default_lead_time_days={defaultLeadTimeDays ?? '—'}
            </span>
            .
          </p>
        </div>
      </div>
    </button>
  );
}
